import React, { useState, useEffect } from 'react';
import { rgba } from 'polished';
import styled, { useTheme } from 'styled-components';
import { Anchor, Flex, Text } from '..';

import Mockoup from '../../../images/features/mockup2.png';

type Props = {
  startY?: number;
  endY?: number | null;
  active?: boolean;
};

const Root = styled.div<Props>`
  position: fixed;
  z-index: 100;
  bottom: 24px;
  right: 16px;
  background: ${({ theme }) => rgba(theme.backgroundColors.primary, 0.9)};
  border: 2px solid ${({ theme }) => theme.borderColors.white};
  border-radius: ${({ theme }) => theme.radii.l};
  backdrop-filter: blur(2px);
  box-shadow: ${({ theme }) => theme.boxShadow.base};
  transform: translateX(90vw);
  transition-duration: ${({ theme }) => theme.transitions.slow};

  &.active {
    transform: translateX(0);
  }

  &:hover {
    opacity: 0.95;
  }

  ${({ theme }) =>
    theme.screen.sp({
      bottom: '16px',
      right: '8px',
    })}
`;

const CloseButton = styled.div`
  position: absolute;
  top: -8px;
  right: -8px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: ${({ theme }) => theme.radii.circle};
  background: ${({ theme }) => theme.backgroundColors.black};
  transform: rotate(45deg);
  transition-duration: ${({ theme }) => theme.transitions.normal};
  cursor: pointer;
  z-index: 1;

  &:hover {
    opacity: 0.8;
    transform: scale(1.1) rotate(45deg);
  }

  &:before {
    position: absolute;
    content: '';
    width: 14px;
    height: 2px;
    background-color: ${({ theme }) => theme.backgroundColors.white};
  }

  &:after {
    position: absolute;
    content: '';
    width: 2px;
    height: 14px;
    background-color: ${({ theme }) => theme.backgroundColors.white};
  }
`;

const Wrapper = styled(Anchor)`
  display: flex;
  justify-content: center;
  flex-align: center;
  gap: ${({ theme }) => theme.spaces.l};
  background: transparent;
  border: none;
  border-radius: ${({ theme }) => theme.radii.l};
  padding: ${({ theme }) => `${theme.spaces.xl} 0 0 ${theme.spaces.xl}`};
  z-index: 0;

  &:hover {
    box-shadow: none;
  }

  ${({ theme }) =>
    theme.screen.sp({
      fontSize: theme.fontSizes.xxs,
      flexDirection: 'row',
      justifyContent: 'center',
    })}
`;

const Title = styled(Text)`
  ${({ theme }) =>
    theme.screen.sp({
      fontSize: theme.fontSizes.s,
      textAlign: 'left',
    })}
`;

const OverrideAnchor = styled.div`
  position: absolute;
  bottom: 16px;
  background: ${({ theme }) => theme.backgroundColors.green};
  color: ${({ theme }) => theme.fontColors.white};
  padding: ${({ theme }) => `${theme.spaces.m} ${theme.spaces.xl}`};
  border-radius: ${({ theme }) => theme.radii.rounded};

  ${({ theme }) =>
    theme.screen.sp({
      fontSize: theme.fontSizes.xs,
      padding: theme.spaces.m,
    })}
`;

const Content = styled(Flex)`
  ${({ theme }) =>
    theme.screen.sp({
      alignItems: 'flex-start',
    })}
`;

const ImageWrap = styled(Flex)`
  position: relative;
  width: 240px;

  gap: ${({ theme }) => theme.spaces.l};
  ${({ theme }) =>
    theme.screen.sp({
      width: '140px',
      gap: theme.spaces.s,
    })}
`;

const Free = styled.div`
  background: ${({ theme }) => theme.backgroundColors.white};
  color: ${({ theme }) => theme.fontColors.primary};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  padding: ${({ theme }) => `${theme.spaces.s} ${theme.spaces.xl}`};
`;

export const FloatingBanner: React.FC<Props> = (props) => {
  const theme = useTheme();

  // スクロール位置を管理するstate
  const [scrollPosition, setScrollPosition] = useState<number>(0);
  // 特定の範囲内にスクロールしたかどうかを管理するstate
  const [isInScrollRange, setIsInScrollRange] = useState<boolean>(false);
  // closeボタンがクリックされたかを管理するstate
  const [isClosed, setIsClosed] = useState<boolean>(false);

  // スクロールイベントのリスナーを追加
  useEffect(() => {
    const handleScroll = () => {
      // closeボタンがクリックされたら何もしない
      if (isClosed) {
        return;
      }

      // 現在のスクロール位置を取得
      const currentPosition = window.scrollY;

      // 特定の範囲内にスクロールしたら状態を変更
      if (currentPosition >= props.startY && currentPosition <= props.endY) {
        setIsInScrollRange(true);
      } else {
        setIsInScrollRange(false);
      }

      // スクロール位置をstateに更新
      setScrollPosition(currentPosition);
    };

    // スクロールイベントをリスナーに追加
    window.addEventListener('scroll', handleScroll);

    // コンポーネントがアンマウントされるときにイベントリスナーを削除
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [props.endY, isClosed]); // isClosedを依存配列に追加

  // closeボタンがクリックされたときの処理
  const handleCloseClick = () => {
    setIsClosed(true);
  };

  return (
    <Root className={isInScrollRange && !isClosed && 'active'}>
      <CloseButton onClick={handleCloseClick} />
      <Wrapper to="/form/demo" id="online-demo">
        <Content
          flexDirection="column"
          alignItems="flex-start"
          justifyContent="center"
          gap={theme.spaces.s}
          mb={theme.spaces.xl}
        >
          <Free>無料</Free>
          <Title color="white" fontWeight="bold" size="xl" lineHeight="m">
            オンライン
            <br />
            デモ体験会
            <br />
            実施中
          </Title>
        </Content>
        <ImageWrap flexDirection="column" alignItems="center">
          <img
            src={Mockoup}
            width="100%"
            height="auto"
            alt="Dentisカルテ画面イメージ"
          />
          <OverrideAnchor>デモ参加はこちら</OverrideAnchor>
        </ImageWrap>
      </Wrapper>
    </Root>
  );
};
